import { categoryColor } from '@/shared/lib/category-color';
/**
 * Server → component shape mappers for the scheduling calendar.
 *
 * The /api/v1/Practice/calendarSlots endpoint returns provider-organized slot
 * data (no patient identity per slot — see types/scheduling-api.ts). The
 * calendar grid components want a flat CalendarAppointment[] keyed by
 * dayIndex + start time. These helpers do the flattening.
 */

import type {
  AppointmentSlot,
  AppointmentStatus,
  AppointmentValueSets,
  AppointmentsDetailsResponse,
  CalendarSlotsResponse,
  CreateAppointmentProfileRequest,
  ListAppointmentResponse,
  ParticipantResponse,
  PracticeWorkingHourList,
  ProviderDetailResponse,
  ProviderIssue,
  ProviderWithSlots,
  WorkingHour,
} from '@/types/scheduling-api';
import type {
  AppointmentStatusType,
  PACalendarAppointment,
  PACalendarProviderSummary,
  PACalendarQuickBookGroup,
} from '@scripsteam/scrips-signal-ds';

// The PA calendar renders the Signal DS PACalendar* family; its profile-bearing
// shapes are generic over the profile type, bound here to the app's wire DTO.
type CalendarAppointment = PACalendarAppointment;
export type ProviderSummary = PACalendarProviderSummary<CreateAppointmentProfileRequest>;
/**
 * A strip practitioner with the slot feed's state (PM `ProviderSummary.slotsState`, `provider-strip.tsx`): `'error'`
 * when the calendarSlots request failed, so the chip and the card say so (and offer Retry) instead of showing figures
 * the feed never gave. Absent when the figures are real.
 */
export type CalendarProviderSummary = ProviderSummary & { slotsState?: 'error' };
export type QuickBookGroup = PACalendarQuickBookGroup<CreateAppointmentProfileRequest>;
import {
  preferredProfileFromList,
  dedupeProfilesPreferRoom,
} from '@/scheduling/lib/quick-create-slot';
import { formatClockParts, formatClockTime } from '@/shared/lib/time-format';
import { practiceNow } from '@/shared/lib/practice-time';

/** Maps the Flutter appointmentType.code → our AppointmentStatusType. */
function statusFromType(t: AppointmentValueSets | undefined): AppointmentStatusType {
  const code = (t?.code ?? '').toLowerCase();
  // Flutter uses these codes — defensive defaults until backend value-set is documented.
  if (code === 'arrived') return 'arrived';
  if (code === 'confirmed' || code === 'booked') return 'confirmed';
  if (code === 'checkedin' || code === 'checked-in') return 'checkedIn';
  if (code === 'noshow' || code === 'no-show') return 'noShow';
  if (code === 'cancelled' || code === 'canceled') return 'cancelled';
  if (code === 'waitlisted') return 'waitlisted';
  return 'booked';
}

/** Slot → tile label. Slots have no patient identity; comment is the closest thing. */
function slotLabel(slot: AppointmentSlot): string {
  if (slot.comment && slot.comment.trim()) return slot.comment.trim();
  if (slot.appointmentType?.display) return slot.appointmentType.display;
  return 'Booked';
}

/** Format start–end as e.g. "9:00a - 9:30a" / "09:00 - 09:30", in the viewer's format (shared/lib/time-format.ts). */
function formatTimeRange(startISO: string, endISO: string): string {
  const fmt = (iso: string) => formatClockTime(new Date(iso));
  return `${fmt(startISO)} - ${fmt(endISO)}`;
}

function durationMinutes(startISO: string, endISO: string): number {
  return Math.max(
    1,
    Math.round((new Date(endISO).getTime() - new Date(startISO).getTime()) / 60000),
  );
}

/** Days between Mon-of-week and the slot's date (0 = Mon … 4 = Fri). */
function dayIndexFromWeekStart(slotISO: string, weekStartISO: string): number {
  const slotDate = new Date(slotISO);
  const weekStart = new Date(weekStartISO);
  // zero out time component on weekStart
  weekStart.setHours(0, 0, 0, 0);
  const diffDays = Math.floor(
    (slotDate.getTime() - weekStart.getTime()) / (1000 * 60 * 60 * 24),
  );
  return diffDays;
}

/**
 * Flattens the provider-organized response into tiles for the WeekCalendar.
 * `weekStartISO` is the Monday (00:00 local) of the week being displayed —
 * dayIndex 0 maps to that day.
 *
 * Slots without a parseable start/end are skipped; this keeps the grid clean
 * if the backend returns half-shaped data.
 */
export function mapCalendarSlotsToWeekAppointments(
  response: CalendarSlotsResponse | undefined,
  weekStartISO: string,
): CalendarAppointment[] {
  if (!response) return [];
  const out: CalendarAppointment[] = [];

  for (const provider of response) {
    const slots = provider.slots ?? [];
    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i];
      if (!slot.start || !slot.end) continue;

      const startDate = new Date(slot.start);
      if (Number.isNaN(startDate.getTime())) continue;

      const dayIndex = dayIndexFromWeekStart(slot.start, weekStartISO);
      // Filter to the week being displayed (5-day work week today).
      if (dayIndex < 0 || dayIndex > 4) continue;

      out.push({
        id: `${provider.id ?? 'p'}-${i}-${slot.start}`,
        patientName: slotLabel(slot),
        status: statusFromType(slot.appointmentType),
        timeRange: formatTimeRange(slot.start, slot.end),
        durationMinutes: durationMinutes(slot.start, slot.end),
        dayIndex,
        startHour: startDate.getHours(),
        startMin: startDate.getMinutes(),
        note: slot.comment,
      });
    }
  }

  return out;
}

/** Same as week, but for a single day (returns slots only for that date). */
export function mapCalendarSlotsToDayAppointments(
  response: CalendarSlotsResponse | undefined,
  dayISO: string,
): CalendarAppointment[] {
  if (!response) return [];
  const target = new Date(dayISO);
  target.setHours(0, 0, 0, 0);
  const targetMs = target.getTime();
  const out: CalendarAppointment[] = [];

  for (const provider of response) {
    const slots = provider.slots ?? [];
    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i];
      if (!slot.start || !slot.end) continue;
      const start = new Date(slot.start);
      if (Number.isNaN(start.getTime())) continue;
      const startDay = new Date(start);
      startDay.setHours(0, 0, 0, 0);
      if (startDay.getTime() !== targetMs) continue;

      out.push({
        id: `${provider.id ?? 'p'}-${i}-${slot.start}`,
        patientName: slotLabel(slot),
        status: statusFromType(slot.appointmentType),
        timeRange: formatTimeRange(slot.start, slot.end),
        durationMinutes: durationMinutes(slot.start, slot.end),
        dayIndex: 0,
        startHour: start.getHours(),
        startMin: start.getMinutes(),
        note: slot.comment,
      });
    }
  }

  return out;
}

/**
 * Returns the Mon-of-current-week and Fri-of-current-week as YYYY-MM-DD.
 * Sunday counts as the previous week's Sunday → Mon is 6 days earlier.
 */
export function workWeekRangeFor(date: Date): {
  monday: Date;
  friday: Date;
  mondayISO: string;
  fridayISO: string;
} {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay(); // 0=Sun, 1=Mon, …, 6=Sat
  const offsetToMonday = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(d);
  monday.setDate(d.getDate() + offsetToMonday);
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  return {
    monday,
    friday,
    mondayISO: monday.toISOString().split('T')[0],
    fridayISO: friday.toISOString().split('T')[0],
  };
}

// ---------------------------------------------------------------------------
// Appointment list → calendar tiles (POST /api/Appointment)
// ---------------------------------------------------------------------------

/** Map server-side AppointmentStatus.code → our AppointmentStatusType. */
function statusFromAppointmentStatus(
  s: AppointmentStatus | undefined,
): AppointmentStatusType {
  const code = (s?.code ?? '').toLowerCase();
  if (code === 'arrived') return 'arrived';
  if (code === 'confirmed') return 'confirmed';
  if (code === 'checkedin' || code === 'checked-in') return 'checkedIn';
  if (code === 'noshow' || code === 'no-show') return 'noShow';
  if (code === 'cancelled' || code === 'canceled') return 'cancelled';
  if (code === 'waitlisted') return 'waitlisted';
  if (code === 'booked') return 'booked';
  return 'booked';
}

function patientNameFromParticipants(
  parts: ParticipantResponse[] | undefined,
): string {
  if (!parts || parts.length === 0) return 'Unknown';
  const p =
    parts.find(
      (x) => x.patient || (x.referenceType ?? '').toLowerCase() === 'patient',
    ) ?? parts[0];
  if (p.patient) {
    return `${p.patient.firstName ?? ''} ${p.patient.lastName ?? ''}`.trim() || 'Unknown';
  }
  return 'Unknown';
}

/**
 * Maps `AppointmentsDetailsResponse.appointments` → tiles for WeekCalendar.
 * `weekStartISO` is the Monday of the displayed week — dayIndex 0 maps to it.
 *
 * Unlike the slot endpoint, /api/Appointment returns full appointment data
 * including patient identity, so tiles render real names.
 */
export function mapAppointmentsToWeekTiles(
  response: AppointmentsDetailsResponse | undefined,
  weekStartISO: string,
  weekLength = 5,
): CalendarAppointment[] {
  const list = response?.appointments;
  if (!list || list.length === 0) return [];
  const out: CalendarAppointment[] = [];

  for (const a of list) {
    if (!a.start || !a.end) continue;
    const startDate = new Date(a.start);
    if (Number.isNaN(startDate.getTime())) continue;

    const dayIndex = dayIndexFromWeekStart(a.start, weekStartISO);
    if (dayIndex < 0 || dayIndex >= weekLength) continue;

    const patient = patientFromParticipants(a.participant);
    out.push({
      id: a.id,
      patientName: patientNameFromParticipants(a.participant),
      status: statusFromAppointmentStatus(a.status),
      timeRange: formatTimeRange(a.start, a.end),
      durationMinutes: durationMinutes(a.start, a.end),
      dayIndex,
      startHour: startDate.getHours(),
      startMin: startDate.getMinutes(),
      note: a.comment,
      isBlock: Boolean(
        (a as ListAppointmentResponse & { isBlock?: boolean }).isBlock,
      ),
      hasNote: Boolean(a.comment && a.comment.trim()),
      hasFlag: Boolean(a.flagList && a.flagList.length > 0),
      isRecurring: Boolean(
        (a as { recurring?: { id?: string }; recurringId?: string }).recurring?.id ||
          (a as { recurringId?: string }).recurringId,
      ),
      patientImportance: patient?.importance?.code,
      patientImportanceDisplay: patient?.importance?.display,
      profileLocation: a.appointmentProfile?.location?.name,
      locationName: a.location?.name,
      profileColor: displayProfileColor(a.appointmentProfile?.color),
      startISO: a.start,
    });
  }

  return out;
}

/**
 * Pull every practitioner id that participates in an appointment.
 * Used to filter the appointment list against the user's checked providers
 * client-side, so the checkbox selection never refires the network query.
 */
export function appointmentProviderIds(
  a: ListAppointmentResponse,
): string[] {
  if (!a.participant) return [];
  const out: string[] = [];
  for (const p of a.participant) {
    const ref = p.referenceType?.toLowerCase();
    const isProvider = Boolean(p.provider) || ref === 'practitioner' || ref === 'provider';
    if (!isProvider) continue;
    const id = p.provider?.id ?? p.reference;
    if (id) out.push(id);
  }
  return out;
}

/**
 * Returns a copy of the response with `appointments` narrowed to those
 * matching at least one of the selected provider ids. Empty selection
 * passes everything through.
 */
export function filterAppointmentsBySelectedProviders(
  response: AppointmentsDetailsResponse | undefined,
  selectedProviderIds: string[],
): AppointmentsDetailsResponse | undefined {
  if (!response) return undefined;
  if (selectedProviderIds.length === 0) return response;
  const set = new Set(selectedProviderIds);
  const appointments = (response.appointments ?? []).filter((a) =>
    appointmentProviderIds(a).some((id) => set.has(id)),
  );
  return { ...response, appointments };
}

/**
 * Client-side status-code filter.
 *
 * Empty selection → pass everything through EXCEPT Cancelled. Cancelled
 * appointments are hidden by default in the day view; the user has to
 * explicitly include 'Cancelled' (via the pill or Statuses dropdown) to
 * see them. Mirrors Flutter's day-view behavior.
 *
 * Non-empty selection → narrow to those codes (case-insensitive on
 * `appointment.status.code`).
 */
export function filterAppointmentsByStatus(
  response: AppointmentsDetailsResponse | undefined,
  selectedCodes: string[],
): AppointmentsDetailsResponse | undefined {
  if (!response) return undefined;
  if (selectedCodes.length === 0) {
    const appointments = (response.appointments ?? []).filter((a) => {
      const code = a.status?.code?.toLowerCase();
      return code !== 'cancelled' && code !== 'canceled';
    });
    if (appointments.length === (response.appointments ?? []).length) {
      return response;
    }
    return { ...response, appointments };
  }
  const set = new Set(selectedCodes.map((c) => c.toLowerCase()));
  const appointments = (response.appointments ?? []).filter((a) => {
    const code = a.status?.code;
    return Boolean(code) && set.has(code.toLowerCase());
  });
  return { ...response, appointments };
}

/**
 * Client-side location filter. Matches `appointment.location.id` against
 * the selected ids. Empty selection passes through (no location filter).
 * Used by the per-location toolbar buttons.
 */
export function filterAppointmentsByLocation(
  response: AppointmentsDetailsResponse | undefined,
  selectedLocationIds: string[],
): AppointmentsDetailsResponse | undefined {
  if (!response) return undefined;
  if (selectedLocationIds.length === 0) return response;
  const set = new Set(selectedLocationIds);
  const appointments = (response.appointments ?? []).filter((a) =>
    Boolean(a.location?.id) && set.has(a.location.id),
  );
  return { ...response, appointments };
}

/**
 * Client-side invoice-status filter — mirrors Flutter's `applyInvoiceFilter`
 * (calendar_tab.dart:1173-1201). The backend doesn't filter by invoice
 * status server-side, so we narrow the response after the fact.
 *
 * Empty selection passes through. Match is case-insensitive on
 * `appointment.invoice.invoiceStatus`. Appointments without an invoice
 * never match a non-empty filter.
 */
export function filterAppointmentsByInvoiceStatus(
  response: AppointmentsDetailsResponse | undefined,
  selectedStatuses: string[],
): AppointmentsDetailsResponse | undefined {
  if (!response) return undefined;
  if (selectedStatuses.length === 0) return response;
  const set = new Set(selectedStatuses.map((s) => s.toLowerCase()));
  const appointments = (response.appointments ?? []).filter((a) => {
    const inv = (
      a as ListAppointmentResponse & { invoice?: { invoiceStatus?: string } }
    ).invoice;
    const status = inv?.invoiceStatus;
    return Boolean(status) && set.has(status!.toLowerCase());
  });
  return { ...response, appointments };
}

// ---------------------------------------------------------------------------
// Day-view list rows  (matches Flutter `calendar_by_day_list_widget.dart`)
// ---------------------------------------------------------------------------

export interface DayListRow {
  id: string;
  startISO: string;
  endISO: string;
  /** "5:20p - 5:50p" */
  timeRange: string;
  room?: string;
  patientName: string;
  /** Patient photo URL (when available). Avatar falls back to initials. */
  patientAvatar?: string;
  /** Patient importance code from `patient.importance.code` (e.g. 'VIP'). */
  patientImportance?: string;
  /** Patient importance display from `patient.importance.display` — the
   *  tile importance-badge tooltip (PROD-990). */
  patientImportanceDisplay?: string;
  /** Mark-Arrived guard inputs (PROD-989) — block the Arrived transition
   *  when the patient's identity/guardian data is incomplete. */
  patientIdentificationId?: string;
  guardianIdentificationId?: string;
  guardianRelationId?: string;
  patientIsMissingData?: boolean;
  patientBirthDateISO?: string;
  /** Optional badges/flags shown next to the name. */
  patientBadge?: string;
  /** ISO date or pre-formatted "Jul 4, 1998" — left blank when absent. */
  dob?: string;
  age?: string;
  doctorName: string;
  doctorPhoto?: string;
  doctorSpecialty?: string;
  /** appointmentProfile.profileName, e.g. "First Appointment - First Ap…" */
  profileName?: string;
  /** reasonCode.display, e.g. "surgery" */
  reasonForVisit?: string;
  status: AppointmentStatusType;
  statusDisplay: string;
  /** Raw backend status code from `appointment.status.code` — drives the
   *  per-row action set in `getRowActionsFor`. Distinct from the
   *  `AppointmentStatusType` above which is our React-side enum. */
  statusCode: string;
  /** Top-level appointment.location.name — used to hide Mark-as-No-Show
   *  for home-visit appointments (Flutter rule). */
  locationName?: string;
  /** True when the appointment carries a recurringId. Hides the Follow-up
   *  action for fulfilled-and-recurring rows. */
  isRecurring: boolean;
  /** Recurring series id when the appointment is part of one — needed by
   *  the cancel-recurring API. */
  recurringId?: string;
  /** Patient id from the participant list — sent in cancel/reschedule
   *  bodies for audit trail. */
  patientId?: string;
  /** Provider id from the participant list — sent in cancel bodies. */
  practitionerId?: string;
  /** appointmentProfile.id from the row — required by the recurring
   *  series-reschedule flow (`useSeriesReschedule` needs all four IDs
   *  — appointmentId + patientId + providerId + appointmentProfileId
   *  — to hit `PreLoadAppointmentDetails`). Added 2026-05-21 for
   *  PROD-853, closing the last entry-point gap from PROD-852. */
  appointmentProfileId?: string;
  note?: string;
  hasNote: boolean;
  hasFlag: boolean;
  /** Hex from appointmentProfile.color — drives the leading color bar. */
  profileColor?: string;
  /** office / home / virtual / block — drives the leading location icon. */
  profileLocation?: string;
  /**
   * True when the row represents a Block Availability entry (not a
   * patient appointment). Drives row-click routing — block rows open
   * `BlockAvailabilityPanel` in edit mode instead of
   * `ViewAppointmentPanel`. Sourced from `ListAppointmentResponse.isBlock`.
   */
  isBlock?: boolean;
}

function patientFromParticipants(parts: ParticipantResponse[] | undefined) {
  if (!parts) return undefined;
  return (
    parts.find(
      (p) => p.patient || (p.referenceType ?? '').toLowerCase() === 'patient',
    )?.patient
  );
}

function providerFromParticipants(parts: ParticipantResponse[] | undefined) {
  if (!parts) return undefined;
  return (
    parts.find(
      (p) =>
        p.provider ||
        (p.referenceType ?? '').toLowerCase() === 'practitioner' ||
        (p.referenceType ?? '').toLowerCase() === 'provider',
    )?.provider
  );
}

/**
 * Format an ISO datetime → "Jul 4, 1998" if it parses, otherwise pass through.
 * Backend may return either an ISO string or a pre-formatted string.
 */
function formatDob(input: string | undefined): string | undefined {
  if (!input) return undefined;
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return input;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a patient age from DOB, mirroring the Flutter / backend rule.
 *
 * Flutter renders `patient.age` straight from the API; the backend formats
 * it. Observed format from the dev server:
 *   ≥ 2y     → "37y"
 *   1y..2y   → "1y 4m 4d"
 *   < 1y     → "11m 16d"
 *
 * This local fallback fires only when the API didn't supply an `age` string —
 * which keeps day/month carryover logic on the server where it belongs.
 */
/**
 * Convert an `appointmentProfile.color` field — which the backend serializes
 * as a Flutter ARGB int (decimal string like "4278231126" = 0xFF00B856) —
 * into a CSS color string.
 *
 * Also tolerates already-hex inputs ("#00B856" or "00B856") so callers don't
 * have to know which format the API used. Returns undefined for anything
 * unparseable, leaving the consumer to fall back to a DS token.
 */
/**
 * The colour a calendar event is DRAWN in: the clinic's stored profile colour read for its hue and drawn
 * in the nearest muted Okabe-Ito step 500 (`--cat-<hue>-500`; Antonin, 13 Oct, both apps). Display only:
 * the stored colour is never rewritten, and a grey / unreadable colour keeps its own value.
 */
export function displayProfileColor(raw: string | undefined): string | undefined {
  const css = profileColorToCss(raw);
  return css ? categoryColor(css, 500, css) : undefined;
}

export function profileColorToCss(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (trimmed.startsWith('#')) return trimmed;
  if (/^[0-9a-fA-F]{6}$/.test(trimmed)) return `#${trimmed}`;
  if (/^[0-9a-fA-F]{8}$/.test(trimmed)) {
    // Hex ARGB (Flutter `Color.value.toRadixString(16)`) — drop alpha.
    return `#${trimmed.slice(2)}`;
  }
  // Flutter `Color.value` as a decimal string — 32-bit ARGB.
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return undefined;
  const rgb = (n & 0xffffff).toString(16).padStart(6, '0').toUpperCase();
  return `#${rgb}`;
}

/**
 * Inverse of {@link profileColorToCss} for the WRITE path: convert a CSS color back to the Flutter
 * `0xFFRRGGBB` (opaque ARGB) string the backend persists and the Flutter clients parse. We only ever
 * read colors as `#RRGGBB` (alpha dropped), so re-apply full opacity (`FF`). Pass through values that
 * are already Flutter-format (`0x…`) or a decimal ARGB so the mapper is idempotent. Undefined → undefined.
 *
 * Why: shipping the CSS `#RRGGBB` form on Book stores a `#`-prefixed string the Flutter app can't
 * parse (`Color(int.parse(…))`), so the appointment/profile shows a broken/default color cross-client.
 */
export function cssColorToProfileWire(css: string | undefined): string | undefined {
  if (!css) return undefined;
  const t = css.trim();
  if (/^0x/i.test(t)) return t.toUpperCase().replace(/^0X/, '0x'); // already Flutter ARGB
  const hex = t.replace(/^#/, '');
  if (/^[0-9a-fA-F]{6}$/.test(hex)) return `0xFF${hex.toUpperCase()}`; // #RRGGBB → opaque ARGB
  if (/^[0-9a-fA-F]{8}$/.test(hex)) return `0x${hex.toUpperCase()}`; // already 8-hex ARGB
  if (/^[0-9]+$/.test(t)) return t; // decimal ARGB string — leave as-is
  // Anything else (e.g. a 3-char `#RGB` shorthand or a CSS name) is NOT a shape our read path ever
  // produces — profileColorToCss only ever yields `#RRGGBB` or a decimal. Pass through unchanged
  // (explicit, non-lossy) rather than guess. Double-conversion is a no-op: a `0x…` input hits the
  // early `/^0x/` branch, so re-applying this never corrupts an already-converted value.
  return t;
}

function ageFromDob(input: string | undefined): string | undefined {
  if (!input) return undefined;
  const birth = new Date(input);
  if (Number.isNaN(birth.getTime())) return undefined;

  const today = new Date();
  let years = today.getFullYear() - birth.getFullYear();
  let months = today.getMonth() - birth.getMonth();
  let days = today.getDate() - birth.getDate();

  // months/years carryover when birthday hasn't occurred yet this year
  if (months < 0 || (months === 0 && days < 0)) {
    years--;
    months += 12;
  }
  // days carryover when current day < DOB day
  if (days < 0) {
    const prevMonthLastDay = new Date(today.getFullYear(), today.getMonth(), 0).getDate();
    days += prevMonthLastDay;
    months--;
  }

  if (years < 0) return undefined;
  if (years >= 2) return `${years}y`;
  if (years >= 1) return `${years}y ${months}m ${days}d`;
  return `${months}m ${days}d`;
}

export function mapAppointmentsToDayList(
  response: AppointmentsDetailsResponse | undefined,
): DayListRow[] {
  const list = response?.appointments;
  if (!list || list.length === 0) return [];
  const rows: DayListRow[] = [];
  for (const a of list) {
    if (!a.start || !a.end) continue;
    const patient = patientFromParticipants(a.participant);
    const provider = providerFromParticipants(a.participant);
    rows.push({
      id: a.id,
      startISO: a.start,
      endISO: a.end,
      timeRange: formatTimeRange(a.start, a.end),
      room: a.room?.name,
      patientName: patient
        ? `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.trim() ||
          'Unknown'
        : 'Unknown',
      patientAvatar: patient?.photo ?? patient?.photoUrl,
      patientImportance: patient?.importance?.code,
      patientImportanceDisplay: patient?.importance?.display,
      patientIdentificationId: patient?.identificationId,
      guardianIdentificationId: (
        patient as { guardianDetails?: { guaardianIdentificationId?: string } } | undefined
      )?.guardianDetails?.guaardianIdentificationId,
      guardianRelationId: (
        patient as { guardianDetails?: { relationId?: string } } | undefined
      )?.guardianDetails?.relationId,
      patientIsMissingData:
        (patient as { isMissingData?: boolean | null } | undefined)?.isMissingData ??
        undefined,
      patientBirthDateISO: patient?.birthDate ?? patient?.dateOfBirth,
      patientBadge: patient?.specialBadge,
      dob: formatDob(patient?.dateOfBirth ?? patient?.birthDate),
      // Prefer the API-supplied pre-formatted age (Flutter parity); fall back
      // to local computation only when the backend didn't include it.
      age: patient?.age ?? ageFromDob(patient?.dateOfBirth ?? patient?.birthDate),
      doctorName: provider?.name ?? '',
      doctorPhoto: provider?.photo,
      doctorSpecialty: specialityDisplay(provider?.speciality),
      profileName: a.appointmentProfile?.profileName,
      reasonForVisit: a.reasonCode?.display,
      status: statusFromAppointmentStatus(a.status),
      statusDisplay: a.status?.display ?? '',
      statusCode: a.status?.code ?? '',
      locationName: a.location?.name,
      isRecurring: Boolean(
        (a as ListAppointmentResponse & { recurringId?: string }).recurringId,
      ),
      recurringId: (a as ListAppointmentResponse & { recurringId?: string })
        .recurringId,
      patientId: patient?.id,
      practitionerId: provider?.id,
      appointmentProfileId: a.appointmentProfile?.id,
      note: a.comment,
      hasNote: Boolean(a.comment && a.comment.trim()),
      hasFlag: Boolean(a.flagList && a.flagList.length > 0),
      profileColor: displayProfileColor(a.appointmentProfile?.color),
      profileLocation: a.appointmentProfile?.location?.name,
      isBlock: Boolean(
        (a as ListAppointmentResponse & { isBlock?: boolean }).isBlock,
      ),
    });
  }
  rows.sort(
    (a, b) =>
      new Date(a.startISO).getTime() - new Date(b.startISO).getTime(),
  );
  return rows;
}

/** Group rows by their start hour (0-23). */
export function groupDayListByHour(
  rows: DayListRow[],
): Map<number, DayListRow[]> {
  const map = new Map<number, DayListRow[]>();
  for (const r of rows) {
    const hour = new Date(r.startISO).getHours();
    const list = map.get(hour);
    if (list) list.push(r);
    else map.set(hour, [r]);
  }
  return map;
}

/** Parse "09:00 AM" / "18:00" / "9 PM" → hour-of-day (0–23). */
function parseTimeStringToHour(input: string | undefined): number | null {
  if (!input) return null;
  const ampmMatch = input.match(/\b(am|pm|AM|PM)\b/);
  const explicitPeriod = ampmMatch ? ampmMatch[1].toLowerCase() : null;
  const cleaned = input.replace(/\b(am|pm|AM|PM)\b/, '').trim();
  const [hStr] = cleaned.split(':');
  let h = Number(hStr);
  if (Number.isNaN(h)) return null;
  if (explicitPeriod === 'pm' && h < 12) h += 12;
  if (explicitPeriod === 'am' && h === 12) h = 0;
  if (h < 0 || h > 23) return null;
  return h;
}

/**
 * Hours to render in the day-view gutter.
 *
 * Mirrors Flutter's `calendar_tab.dart` logic exactly:
 *   1. Take every PracticeWorkingHourList entry where `type === 1`
 *      (the standard work-hours profile; other types are breaks/closures).
 *   2. Within those, match each `practiceWorkingSlots[]` entry to today's
 *      weekday (`weedayId`).
 *   3. earliest start hour … latest end hour (inclusive) across matches.
 *   4. Extend the range to cover any appointments scheduled outside that
 *      window (so out-of-hours bookings are still visible).
 *   5. Fall back to 8a–8p if no practice working-hours data is available.
 */
export function hoursForDayList(
  rows: DayListRow[],
  practiceWorkingHours: PracticeWorkingHourList[] | undefined,
  forDate: Date,
): number[] {
  const jsDow = forDate.getDay();
  const weedayId = jsDow === 0 ? 7 : jsDow;
  const dayName = forDate
    .toLocaleDateString('en-US', { weekday: 'long' })
    .toLowerCase();

  let earliestStart: number | null = null;
  let latestEnd: number | null = null;

  const workProfiles = (practiceWorkingHours ?? []).filter(
    (p) => p.type === 1,
  );
  for (const profile of workProfiles) {
    for (const slot of profile.practiceWorkingSlots ?? []) {
      const matchesDay =
        slot.weedayId === weedayId ||
        (slot.startWeekDay ?? '').toLowerCase() === dayName ||
        (slot.endWeekDay ?? '').toLowerCase() === dayName;
      if (!matchesDay) continue;
      const start = parseTimeStringToHour(slot.startTime);
      const end = parseTimeStringToHour(slot.endTime);
      if (start !== null) {
        earliestStart = earliestStart === null ? start : Math.min(earliestStart, start);
      }
      if (end !== null) {
        latestEnd = latestEnd === null ? end : Math.max(latestEnd, end);
      }
    }
  }

  // Extend the visible range to include any outlier appointments.
  for (const r of rows) {
    const h = new Date(r.startISO).getHours();
    if (earliestStart === null || h < earliestStart) earliestStart = h;
    if (latestEnd === null || h > latestEnd) latestEnd = h;
  }

  // Fallback: 8a – 8p if we have no signal at all.
  if (earliestStart === null || latestEnd === null) {
    earliestStart = 8;
    latestEnd = 20;
  }

  const out: number[] = [];
  for (let h = earliestStart; h <= latestEnd; h++) out.push(h);
  return out;
}

/** Single-day variant — maps the same response, filtering to one date. */
export function mapAppointmentsToDayTiles(
  response: AppointmentsDetailsResponse | undefined,
  dayISO: string,
): CalendarAppointment[] {
  const list = response?.appointments;
  if (!list || list.length === 0) return [];
  const target = new Date(dayISO);
  target.setHours(0, 0, 0, 0);
  const targetMs = target.getTime();
  const out: CalendarAppointment[] = [];

  for (const a of list) {
    if (!a.start || !a.end) continue;
    const start = new Date(a.start);
    if (Number.isNaN(start.getTime())) continue;
    const startDay = new Date(start);
    startDay.setHours(0, 0, 0, 0);
    if (startDay.getTime() !== targetMs) continue;

    const patient = patientFromParticipants(a.participant);
    out.push({
      id: a.id,
      patientName: patientNameFromParticipants(a.participant),
      status: statusFromAppointmentStatus(a.status),
      timeRange: formatTimeRange(a.start, a.end),
      durationMinutes: durationMinutes(a.start, a.end),
      dayIndex: 0,
      startHour: start.getHours(),
      startMin: start.getMinutes(),
      note: a.comment,
      isBlock: Boolean(
        (a as ListAppointmentResponse & { isBlock?: boolean }).isBlock,
      ),
      hasNote: Boolean(a.comment && a.comment.trim()),
      hasFlag: Boolean(a.flagList && a.flagList.length > 0),
      isRecurring: Boolean(
        (a as { recurring?: { id?: string }; recurringId?: string }).recurring?.id ||
          (a as { recurringId?: string }).recurringId,
      ),
      patientImportance: patient?.importance?.code,
      patientImportanceDisplay: patient?.importance?.display,
      profileLocation: a.appointmentProfile?.location?.name,
      locationName: a.location?.name,
      profileColor: displayProfileColor(a.appointmentProfile?.color),
      startISO: a.start,
    });
  }

  return out;
}

// ---------------------------------------------------------------------------
// Provider strip enrichment
// ---------------------------------------------------------------------------

/**
 * Format a time-of-day string → e.g. "9:00a" / "09:00", in the viewer's format. Accepts:
 *   "09:00:00"        24-h with seconds
 *   "09:00"           24-h
 *   "9:00 AM" / "9 PM" 12-h with am/pm marker
 *   ISO datetimes ("2026-05-05T09:00:00") — uses just the time portion
 */
function formatTimeOfDay(input: string | undefined): string {
  if (!input) return '';

  // ISO datetime — pull the time portion via Date
  if (input.includes('T')) {
    const d = new Date(input);
    if (!Number.isNaN(d.getTime())) return formatClockTime(d);
  }

  // Strip am/pm marker if present
  const ampmMatch = input.match(/\b(am|pm|AM|PM)\b/);
  const explicitPeriod = ampmMatch ? ampmMatch[1].toLowerCase() : null;
  const cleaned = input.replace(/\b(am|pm|AM|PM)\b/, '').trim();

  const [hStr, mStr] = cleaned.split(':');
  let h = Number(hStr);
  const m = Number(mStr ?? '0');
  if (Number.isNaN(h) || Number.isNaN(m)) return '';

  if (explicitPeriod === 'am' && h === 12) h = 0; // "12 AM" is midnight
  else if (explicitPeriod === 'pm' && h < 12) h += 12; // normalize 12-h "1 PM" to 13
  return formatClockParts(h, m);
}

/**
 * Pick the working-hours window for `forDate` and return e.g. "09:00 - 18:00".
 * Tries: weedayId match (1=Mon … 7=Sun) → startWeekDay/endWeekDay name match
 * → fall through to first entry. Returns "" only when truly nothing parses.
 */
function workingHoursLabel(
  hours: WorkingHour[] | undefined,
  forDate: Date,
): string {
  if (!hours || hours.length === 0) return '';

  // Sun=0…Sat=6 from JS; Flutter weedayId convention is typically Mon=1…Sun=7
  const jsDow = forDate.getDay();
  const weedayId = jsDow === 0 ? 7 : jsDow;
  const dayName = forDate.toLocaleDateString('en-US', { weekday: 'long' });

  const match =
    hours.find((h) => h.weedayId === weedayId) ??
    hours.find(
      (h) =>
        (h.startWeekDay ?? '').toLowerCase() === dayName.toLowerCase() ||
        (h.endWeekDay ?? '').toLowerCase() === dayName.toLowerCase(),
    ) ??
    hours[0];

  // Some backends use start/end instead of startTime/endTime — read either.
  const rawStart =
    match.startTime ??
    (match as unknown as { start?: string }).start;
  const rawEnd =
    match.endTime ?? (match as unknown as { end?: string }).end;

  const start = formatTimeOfDay(rawStart);
  const end = formatTimeOfDay(rawEnd);
  if (!start || !end) return '';
  return `${start} - ${end}`;
}

function isSameDay(aISO: string, b: Date): boolean {
  const a = new Date(aISO);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Format an ISO datetime → just the time portion, e.g. "4:55p". */
function formatSlotTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  let h = d.getHours();
  const m = d.getMinutes();
  const period = h >= 12 ? 'p' : 'a';
  h = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h}:00${period}` : `${h}:${String(m).padStart(2, '0')}${period}`;
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Group appointment profiles by location category (office / home / virtual /
 * block). Mirrors Flutter's `groupedAppointmentProfile` Map at calendar_tab.
 * dart:4098-4116 — profiles are first sorted alphabetically by
 * `appointmentType.display`, THEN grouped by `location.name`. The chip
 * popover preserves that order, so quick-book entries land in the same
 * sequence dev shows.
 *
 * Profiles without a location fall into an "office" bucket as a sensible
 * default — this matches Flutter's IconHelper.getLocationIcon fallback.
 */
/**
 * The provider's preferred-or-first profile for quick-create (PROD-983),
 * selected from the RAW original-order list (preserving Flutter's
 * `appointmentProfile.where(isPreferred).first ?? .first`), with its color
 * CSS-normalized so it matches the profile objects the provider-strip chips
 * pass into `handleQuickCreate` (same `buildProfileFormValues` consumer).
 * Deliberately does NOT read `quickBookGroups` — that list is re-sorted +
 * regrouped (review §2).
 */
function pickPreferredProfileNormalized(
  profiles: CreateAppointmentProfileRequest[] | undefined,
): CreateAppointmentProfileRequest | null {
  const pp = preferredProfileFromList(profiles);
  return pp ? { ...pp, color: profileColorToCss(pp.color) ?? '' } : null;
}

function groupProfilesByLocation(
  profiles: CreateAppointmentProfileRequest[] | undefined,
): QuickBookGroup[] {
  if (!profiles || profiles.length === 0) return [];

  // Stable copy — sort alphabetically by appointmentType.display ascending.
  // Empty / missing display sinks to the top (matches Dart's null-as-empty
  // string comparison).
  const sorted = [...profiles].sort((a, b) =>
    (a.appointmentType?.display ?? '').localeCompare(
      b.appointmentType?.display ?? '',
    ),
  );

  const groups = new Map<string, CreateAppointmentProfileRequest[]>();
  for (const profile of sorted) {
    const key = (profile.location?.name ?? 'office').toLowerCase();
    const list = groups.get(key);
    if (list) {
      list.push(profile);
    } else {
      groups.set(key, [profile]);
    }
  }

  return Array.from(groups.entries()).map(([location, ps]) => {
    // Pick the chip's representative: the provider-preferred profile if
    // any, else the first (already sorted by appointmentType.display).
    // Mirrors Flutter calendar_tab.dart:4324:
    //   preferredApptProfile = profiles.where(isPreferred).firstOrElse(first)
    const preferred = ps.find((p) => p.isPreferred === true) ?? ps[0];
    return {
      location,
      durationMinutes: preferred.minutesDuration ?? 0,
      // Backend ships color as a Flutter ARGB int string ("4278231126") —
      // normalize to CSS so the chip can apply it directly. Falls back
      // to '' rather than undefined so the QuickBookGroup color field
      // stays `string` (consumers expect a CSS string, not optional).
      color: profileColorToCss(preferred.color) ?? '',
      profiles: ps.map((p) => ({ ...p, color: profileColorToCss(p.color) ?? '' })),
    };
  });
}

/**
 * Merge `useAvailableProviders` (basic provider list) with `useCalendarSlots`
 * (working hours + slots) to produce the full ProviderSummary the UI expects.
 *
 * Slot-derived fields (slotsToday, schedule, nextSlots, available) are filled
 * from the calendarSlots match by id; if no match, they fall back to safe
 * defaults so the provider still renders.
 */
export function enrichProviders(
  available: ProviderDetailResponse[] | undefined,
  slotsResponse: CalendarSlotsResponse | undefined,
  today: Date = practiceNow(),
): ProviderSummary[] {
  if (!available || available.length === 0) return [];

  const slotsByProviderId = new Map<string, ProviderWithSlots>();
  for (const p of slotsResponse ?? []) {
    if (p.id) slotsByProviderId.set(p.id, p);
  }

  return available.map((p) => {
    const enriched = slotsByProviderId.get(p.id);
    const slots = enriched?.slots ?? [];

    const todaySlots = slots.filter(
      (s): s is AppointmentSlot & { start: string } =>
        Boolean(s.start) && isSameDay(s.start as string, today),
    );

    const nowMs = today.getTime();
    const upcomingSlots = slots
      .filter(
        (s): s is AppointmentSlot & { start: string } =>
          Boolean(s.start) && new Date(s.start as string).getTime() >= nowMs,
      )
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
      .slice(0, 3);
    const upcoming = upcomingSlots.map((s) => formatSlotTime(s.start));

    const schedule = workingHoursLabel(enriched?.workingHours, today);

    return {
      id: p.id,
      name: p.name,
      specialty: p.speciality?.display ?? '',
      initials: initialsOf(p.name),
      photo: p.photo ?? enriched?.photo,
      // Credential/license issue badge (PROD-980) — pass the backend flag
      // (present only when the provider has an issue) straight through.
      issue: p.issue ?? null,
      // "available" today = has any working-hours window for today.
      available: schedule !== '',
      slotsToday: enriched?.totalSlots ?? todaySlots.length,
      schedule,
      nextSlots: upcoming,
      // Raw slots for the clickable "Next" chips — PROD-829 quick-create
      // path B (Flutter `calendar_tab.dart:4514, 4552, 4615`).
      nextSlotResponses: upcomingSlots,
      // Collapse phantom-room duplicate profiles before grouping + preferred
      // selection (room-fix defense-in-depth, 2026-06-03) so the chips +
      // quick-create don't book the dead twin. See dedupeProfilesPreferRoom.
      quickBookGroups: groupProfilesByLocation(
        dedupeProfilesPreferRoom(enriched?.appointmentProfile),
      ),
      preferredProfile: pickPreferredProfileNormalized(
        dedupeProfilesPreferRoom(enriched?.appointmentProfile),
      ),
    };
  });
}

/**
 * Build ProviderSummary[] purely from the calendarSlots response.
 *
 * calendarSlots is the richer source — it carries name/photo/specialty PLUS
 * working hours, slot list, and appointment profiles. When it has data, we
 * prefer it over availableProviders so the strip never flickers from
 * "enriched" → "basic" if availableProviders arrives later.
 */
export function providersFromCalendarSlots(
  slotsResponse: CalendarSlotsResponse | undefined,
  today: Date = practiceNow(),
): ProviderSummary[] {
  if (!slotsResponse || slotsResponse.length === 0) return [];

  const nowMs = today.getTime();

  return slotsResponse
    .filter((p): p is ProviderWithSlots & { id: string; name: string } =>
      Boolean(p.id && p.name),
    )
    .map((p) => {
      const slots = p.slots ?? [];

      const todaySlots = slots.filter(
        (s): s is AppointmentSlot & { start: string } =>
          Boolean(s.start) && isSameDay(s.start as string, today),
      );

      const upcomingSlots = slots
        .filter(
          (s): s is AppointmentSlot & { start: string } =>
            Boolean(s.start) && new Date(s.start as string).getTime() >= nowMs,
        )
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
        .slice(0, 3);
      const upcoming = upcomingSlots.map((s) => formatSlotTime(s.start));

      const schedule = workingHoursLabel(p.workingHours, today);

      return {
        id: p.id,
        name: p.name,
        specialty: p.speciality?.display ?? '',
        initials: initialsOf(p.name),
        photo: p.photo,
        // Credential/license issue badge (PROD-980). The slots-grouped
        // provider shape doesn't type `issue`; read it loosely so it shows
        // if the endpoint carries it, no-op otherwise.
        issue: (p as { issue?: ProviderIssue | null }).issue ?? null,
        available: schedule !== '',
        slotsToday: p.totalSlots ?? todaySlots.length,
        schedule,
        nextSlots: upcoming,
        nextSlotResponses: upcomingSlots,
        // Phantom-room duplicate guard (room-fix, 2026-06-03) — see enrichProviders.
        quickBookGroups: groupProfilesByLocation(
          dedupeProfilesPreferRoom(p.appointmentProfile),
        ),
        preferredProfile: pickPreferredProfileNormalized(
          dedupeProfilesPreferRoom(p.appointmentProfile),
        ),
      };
    });
}

/**
 * Builds the specialty filter chip list from the provider response.
 * Always prepends "All". Dedupes by display string, sorts alphabetically.
 */
/**
 * Pull a human-readable display string off a Speciality-shaped value,
 * regardless of which exact field the backend used. Real responses have
 * been observed with `.display`, `.code.display`, `.displayName`,
 * `.name`, and even nested `.code.code` for the legacy CodeList shape.
 */
export function specialityDisplay(s: unknown): string | undefined {
  if (!s || typeof s !== 'object') return undefined;
  const obj = s as Record<string, unknown>;
  if (typeof obj.display === 'string' && obj.display) return obj.display;
  if (typeof obj.displayName === 'string' && obj.displayName) return obj.displayName;
  if (typeof obj.name === 'string' && obj.name) return obj.name;
  const code = obj.code as Record<string, unknown> | undefined;
  if (code && typeof code === 'object') {
    if (typeof code.display === 'string' && code.display) return code.display;
    if (typeof code.displayName === 'string' && code.displayName) return code.displayName;
  }
  return undefined;
}

/**
 * Every specialty (primary + multi) a provider carries, deduped.
 * Used to filter providers when the user picks chips — a provider stays
 * visible if ANY of their specialties matches the user's selection.
 */
export function getProviderSpecialties(
  p: { speciality?: unknown; doctorSpecialities?: unknown[] } | undefined,
): string[] {
  if (!p) return [];
  const set = new Set<string>();
  const primary = specialityDisplay(p.speciality);
  if (primary) set.add(primary);
  for (const s of p.doctorSpecialities ?? []) {
    const d = specialityDisplay(s);
    if (d) set.add(d);
  }
  return Array.from(set);
}

/**
 * True if the provider has at least one specialty in the selected list.
 * If `selected` is empty (== "All"), every provider matches.
 */
export function providerMatchesSpecialties(
  p: { speciality?: unknown; doctorSpecialities?: unknown[] } | undefined,
  selected: string[],
): boolean {
  if (selected.length === 0) return true;
  const provSpecs = getProviderSpecialties(p);
  return selected.some((s) => provSpecs.includes(s));
}

export function specialtiesFromProviders(
  providers: ProviderDetailResponse[] | undefined,
): string[] {
  if (!providers || providers.length === 0) return ['All'];
  const set = new Set<string>();
  for (const p of providers) {
    const primary = specialityDisplay(p.speciality);
    if (primary) set.add(primary);
    for (const s of p.doctorSpecialities ?? []) {
      const d = specialityDisplay(s);
      if (d) set.add(d);
    }
  }
  return ['All', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
}

/**
 * Same as `specialtiesFromProviders` but reads off the calendarSlots shape.
 * Mirrors Flutter (`doctors.expand((d) => d.doctorSpecialities ?? [])`):
 * a doctor can carry multiple specialties, so the full chip list comes
 * from flattening `doctorSpecialities` across every provider, not just
 * each one's primary `speciality`.
 */
export function specialtiesFromCalendarSlots(
  slotsResponse: CalendarSlotsResponse | undefined,
): string[] {
  if (!slotsResponse || slotsResponse.length === 0) return ['All'];
  const set = new Set<string>();
  for (const p of slotsResponse) {
    const primary = specialityDisplay(p.speciality);
    if (primary) set.add(primary);
    for (const s of p.doctorSpecialities ?? []) {
      const d = specialityDisplay(s);
      if (d) set.add(d);
    }
  }
  return ['All', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
}

/**
 * Builds the 5-cell day strip header (Mon→Fri) for the week containing `date`.
 * Marks the cell whose date matches `today`.
 */
export function buildWorkWeekDays(
  date: Date,
  today: Date = practiceNow(),
): { dayOfWeek: string; date: number; isToday?: boolean }[] {
  const { monday } = workWeekRangeFor(date);
  const todayStripped = new Date(today);
  todayStripped.setHours(0, 0, 0, 0);
  const todayMs = todayStripped.getTime();
  const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
  return labels.map((label, i) => {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    const dayMs = new Date(day).setHours(0, 0, 0, 0);
    return {
      dayOfWeek: label,
      date: day.getDate(),
      ...(dayMs === todayMs ? { isToday: true } : {}),
    };
  });
}
